import { DialogContent, DialogActions, Button, Typography, Box, Divider, Modal } from '@mui/material';
import { WarningFilled } from '@ant-design/icons';
import Avatar from 'components/@extended/Avatar';
import MainCard from 'components/MainCard';
import SimpleBar from 'components/third-party/SimpleBar';
import { useMemo } from 'react';
import { fromppfUserTypeFlag } from './UserForm';

interface RemoveRoleConfirmationProps {
  open: boolean;
  onClose: () => void;
  selectedUserTypes: number[];
  ppfUserType?: number;
  onConfirm: () => void;
}

const RemoveRoleConfirmation = ({ open, onClose, selectedUserTypes, ppfUserType, onConfirm }: RemoveRoleConfirmationProps) => {
  const description = useMemo(() => {
    const userTypes = fromppfUserTypeFlag(ppfUserType || 0);

    if (!selectedUserTypes.includes(4) && !selectedUserTypes.includes(8) && userTypes.includes(4) && userTypes.includes(8)) {
      return 'You are removing Plan Reviewer and Inspector roles. All related permit configurations will be deleted.';
    } else if (!selectedUserTypes.includes(4) && userTypes.includes(4)) {
      return 'You are removing Plan Reviewer role. All plan review permit configurations for this user will be deleted.';
    } else if (!selectedUserTypes.includes(8) && userTypes.includes(8)) {
      return 'You are removing Inspector role. All inspection permit configurations for this user will be deleted.';
    }
  }, [selectedUserTypes, ppfUserType]);

  return (
    <Modal
      open={open}
      onClose={(event, reason) => {
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
        onClose();
      }}
      sx={{
        '& .MuiPaper-root:focus': {
          outline: 'none'
        }
      }}
    >
      <MainCard
        sx={{
          minWidth: { xs: 320, sm: 400, md: 511, lg: 511 },
          maxWidth: { xs: 400, lg: 511 },
          height: 'auto',
          maxHeight: 'calc(100vh - 48px)'
        }}
        modal
        content={false}
      >
        <SimpleBar sx={{ maxHeight: `calc(100vh - 48px)`, '& .simplebar-content': { display: 'flex', flexDirection: 'column' } }}>
          <DialogContent sx={{ padding: 3 }}>
            <Box textAlign="center" pb="34px">
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: '50%',
                  bgcolor: 'warning.lighter',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: 'auto'
                }}
              >
                <Avatar color="warning">
                  <WarningFilled style={{ fontSize: 30 }} />
                </Avatar>
              </Box>

              <Typography fontSize={20} fontWeight={600} mt={3}>
                Remove Role Confirmation
              </Typography>

              <Typography fontSize={16} color="text.secondary" mt={1} sx={{ whiteSpace: 'pre-line' }}>
                {description?.replaceAll('. ', '.\n')}
              </Typography>
            </Box>
            <Divider />
          </DialogContent>
          <DialogActions sx={{ padding: 3, paddingTop: 0 }}>
            <Box display="flex" gap={3}>
              <Button variant="outlined" color="secondary" onClick={onClose} sx={{ padding: '10px 32px', fontSize: 16 }}>
                Cancel
              </Button>

              <Button variant="contained" color="warning" type="submit" onClick={onConfirm} sx={{ padding: '10px 32px', fontSize: 16 }}>
                Confirm
              </Button>
            </Box>
          </DialogActions>
        </SimpleBar>
      </MainCard>
    </Modal>
  );
};

export default RemoveRoleConfirmation;
